import React from 'react';
import { UX2 } from '@wsb/guac-widget-core';
import styles from './styles';
import { BUTTON_ICON_SIZE, BUTTON_ICON_SIZE_SM } from '../../../../../constants';

/*
 * If improvedIconButton=true AND (leftIcon or rightIcon) is provided, then improvedIconChildren will be used instead of iconChildren.
 * Otherwise, iconChildren is used when `icon` is defined. If neither are defined, children will be used.
 * improvedIconChildren is a flexbox with leftIcon, text, rightIcon. uses leftIcon and rightIcon.
 * iconChildren is a flexbox with icon and text; uses iconOnLeft to determine order of icon and text.
 */
export default function Button({ children, ...props }) {
  const { state = 'default', size = 'default', leftIcon, rightIcon, improvedIconButton } = props;
  const tag = props.href || props.target || props.theme === 'link' ? 'a' : 'button';
  const externalLinkProps = tag === 'a' && props.target === '_blank' ? { rel: 'noopener' } : {};
  const iconSizeDefault = size === 'small' ? 'small' : 'default';
  const {
    icon,
    iconOnLeft = true,
    iconSize = iconSizeDefault,
    ...styleProps
  } = styles.call(this, props);
  const btnAddonEl = props.btnAddon ? <UX2.Element.Element className='btnAddon' /> : null;

  // Icon logic
  const iconSizes = {
    default: {
      icon: BUTTON_ICON_SIZE,
      adjustment: '-xsmall'
    },
    small: {
      icon: BUTTON_ICON_SIZE_SM,
      adjustment: '-xxsmall'
    }
  };
  const iconAdjustStyles =
    styleProps.fill !== 'OPEN'
      ? {
        marginLeft: iconOnLeft ? iconSizes[iconSize].adjustment : 0,
        marginRight: iconOnLeft ? 0 : iconSizes[iconSize].adjustment
      }
      : {};
  const iconSpace = `calc(${iconSizes[iconSize].icon} + 8px)`;
  const iconStyles = {
    common: {
      fontSize: 'inherit',
      fontFamily: 'inherit',
      textTransform: 'inherit',
      letterSpacing: 'inherit'
    },
    wrapper: {
      position: 'relative',
      ...iconAdjustStyles
    },
    icon: {
      width: iconSizes[iconSize].icon,
      height: iconSizes[iconSize].icon,
      position: 'absolute',
      top: '50%',
      left: iconOnLeft ? 0 : 'auto',
      right: iconOnLeft ? 'auto' : 0,
      lineHeight: 0,
      transform: 'translateY(-50%)',
      ['> img']: {
        display: 'block'
      }
    },
    text: {
      display: 'block',
      textAlign: 'center',
      marginLeft: iconOnLeft ? iconSpace : 0,
      marginRight: iconOnLeft ? 0 : iconSpace
    }
  };

  const iconChildren = (
    <React.Fragment>
      { btnAddonEl }
      <UX2.Element.Element style={{ ...iconStyles.common, ...iconStyles.wrapper }}>
        <UX2.Element.Element style={ iconStyles.icon }>
          { typeof icon === 'string' ? (
            <UX2.Element.Icon icon={ icon } size={ iconSizes[iconSize].icon } />
          ) : (
            icon
          ) }
        </UX2.Element.Element>
        <UX2.Element.Element
          className='iconText'
          style={{ ...iconStyles.common, ...iconStyles.text }}
        >
          { children }
        </UX2.Element.Element>
      </UX2.Element.Element>
    </React.Fragment>
  );

  let improvedIconChildren = null;

  if (improvedIconButton && (leftIcon || rightIcon)) {
    const improvedIconStyles = {
      wrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      },
      iconBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      },
      text: {
        flex: 1,
        alignSelf: 'center',
        paddingHorizontal: 'small',
        textAlign: 'center'
      }
    };

    improvedIconChildren = (
      <React.Fragment>
        { btnAddonEl }
        <UX2.Element.Element className='iconWrapper' style={ improvedIconStyles.wrapper }>
          { leftIcon && (
            <UX2.Element.Element className='iconBox' style={ improvedIconStyles.iconBox }>
              { typeof leftIcon === 'string' ? (
                <UX2.Element.Icon icon={ leftIcon } size='28' />
              ) : (
                leftIcon
              ) }
            </UX2.Element.Element>
          ) }
          <UX2.Element.Element
            typography='ButtonAlpha'
            className='iconText'
            size={ size }
            style={ improvedIconStyles.text }
          >
            { children }
          </UX2.Element.Element>
          { rightIcon && (
            <UX2.Element.Element className='iconBox' style={ improvedIconStyles.iconBox }>
              { typeof rightIcon === 'string' ? (
                <UX2.Element.Icon icon={ rightIcon } size='28' />
              ) : (
                rightIcon
              ) }
            </UX2.Element.Element>
          ) }
        </UX2.Element.Element>
      </React.Fragment>
    );
  }

  return this.merge(
    {
      tag,
      disabled: state === 'disabled',
      tcclTracking: 'click',
      typography: 'ButtonAlpha',
      ...externalLinkProps,
      children:
        improvedIconButton && (leftIcon || rightIcon) ? (
          improvedIconChildren
        ) : icon ? (
          iconChildren
        ) : (
          <React.Fragment>
            { btnAddonEl }
            { children }
          </React.Fragment>
        ),
      ['data-ux-btn']: this.base.kind.toLowerCase()
    },
    styleProps,
    props
  );
}
